import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import BrandLogo from "@/components/BrandLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import TipTapEditor from "@/components/TipTapEditor";
import AttachmentUploader from "@/components/AttachmentUploader";
import { ArrowRight, Save, Plus, Trash2, FileText, ClipboardCheck } from "lucide-react";
import { toast } from "sonner";

export default function TopicForm() {
    const nav = useNavigate();
    const { id } = useParams();
    const editing = Boolean(id);
    const { user } = useAuth();
    const [classes, setClasses] = useState([]);
    const [categories, setCategories] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [title, setTitle] = useState("");
    const [section, setSection] = useState("quantitative");
    const [categoryId, setCategoryId] = useState("");
    const [selectedClasses, setSelectedClasses] = useState([]);
    const [content, setContent] = useState("");
    const [attachments, setAttachments] = useState([]);
    const [quiz, setQuiz] = useState({ enabled: false, questions: [] });
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        api.get("/trainer/classes").then((r) => setClasses(r.data)).catch(() => {});
        api.get("/topic-templates").then((r) => setTemplates(r.data)).catch(() => {});
        if (editing) {
            api.get(`/topics/${id}`).then((r) => {
                const t = r.data;
                setTitle(t.title); setSection(t.section);
                setSelectedClasses(t.class_ids); setContent(t.content_html || "");
                setAttachments(t.attachments || []); setCategoryId(t.category_id || "");
                setQuiz(t.quiz && t.quiz.questions?.length ? { enabled: true, questions: t.quiz.questions } : { enabled: false, questions: [] });
            }).catch((e) => toast.error(formatApiError(e)));
        }
    }, [editing, id]);

    useEffect(() => {
        api.get("/categories", { params: { section } }).then((r) => setCategories(r.data)).catch(() => {});
    }, [section]);

    const toggle = (cid) => setSelectedClasses((v) => v.includes(cid) ? v.filter((x) => x !== cid) : [...v, cid]);
    const selectAll = () => setSelectedClasses(selectedClasses.length === classes.length ? [] : classes.map((c) => c.id));

    const applyTemplate = (tplId) => {
        const t = templates.find((x) => x.id === tplId);
        if (!t) return;
        if (!title) setTitle(t.title_template);
        setContent(t.content_html);
        toast.success(`تم تطبيق قالب "${t.name}"`);
    };

    const addQ = () => setQuiz((q) => ({ ...q, questions: [...q.questions, { id: crypto.randomUUID(), text: "", choices: ["", "", "", ""], correct_index: 0 }] }));
    const rmQ = (idx) => setQuiz((q) => ({ ...q, questions: q.questions.filter((_, i) => i !== idx) }));
    const updQ = (idx, patch) => setQuiz((q) => ({ ...q, questions: q.questions.map((qq, i) => i === idx ? { ...qq, ...patch } : qq) }));
    const updChoice = (qi, ci, v) => setQuiz((q) => ({ ...q, questions: q.questions.map((qq, i) => i === qi ? { ...qq, choices: qq.choices.map((c, j) => j === ci ? v : c) } : qq) }));

    const submit = async () => {
        if (!title.trim()) return toast.error("العنوان مطلوب");
        if (selectedClasses.length === 0) return toast.error("اختاري فصلاً واحداً على الأقل");
        let quizPayload = null;
        if (quiz.enabled && quiz.questions.length > 0) {
            for (const q of quiz.questions) {
                if (!q.text.trim()) return toast.error("نص السؤال مطلوب لكل سؤال");
                if (q.choices.some((c) => !c.trim())) return toast.error("جميع الخيارات مطلوبة");
            }
            quizPayload = { questions: quiz.questions };
        }
        setBusy(true);
        try {
            const payload = {
                title: title.trim(), section, class_ids: selectedClasses,
                content_html: content, attachments,
                category_id: categoryId || null, quiz: quizPayload,
            };
            if (editing) await api.patch(`/topics/${id}`, payload);
            else await api.post("/topics", payload);
            toast.success(editing ? "تم حفظ التعديلات" : "تم نشر الموضوع");
            nav("/trainer/topics");
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    return (
        <div dir="rtl" className="min-h-screen bg-slate-50">
            <header className="bg-[#0F172A] text-white sticky top-0 z-10">
                <div className="max-w-5xl mx-auto px-6 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <Button variant="ghost" size="sm" onClick={() => nav("/trainer/topics")} className="text-white/80 hover:text-white hover:bg-white/10"><ArrowRight className="w-4 h-4 ml-1" /> رجوع</Button>
                        <BrandLogo size={36} />
                    </div>
                </div>
            </header>
            <main className="max-w-5xl mx-auto p-6 space-y-5">
                <div className="flex items-start justify-between gap-3">
                    <div>
                        <h1 className="text-2xl font-bold text-[#0F172A]">{editing ? "تعديل موضوع" : "إضافة موضوع جديد"}</h1>
                        <p className="text-sm text-slate-500 mt-1">اكتبي، نسّقي، أرفقي، أضيفي اختباراً — ثم انشري</p>
                    </div>
                    {!editing && (
                        <Select onValueChange={applyTemplate}>
                            <SelectTrigger className="w-56" data-testid="template-select"><SelectValue placeholder="استخدمي قالباً..." /></SelectTrigger>
                            <SelectContent>{templates.map((t) => <SelectItem key={t.id} value={t.id}><FileText className="w-3 h-3 inline ml-1" /> {t.name}</SelectItem>)}</SelectContent>
                        </Select>
                    )}
                </div>

                <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-5">
                    <div>
                        <Label className="mb-1.5 block">عنوان الموضوع</Label>
                        <Input data-testid="topic-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="مثال: النسبة والتناسب - تدريب 1" className="h-11 text-base" />
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                        <div>
                            <Label className="mb-2 block">القسم</Label>
                            <RadioGroup value={section} onValueChange={setSection} className="flex gap-6" data-testid="topic-section">
                                <label className="flex items-center gap-2 cursor-pointer"><RadioGroupItem value="quantitative" data-testid="section-quantitative" /><span>القدرات الكمية</span></label>
                                <label className="flex items-center gap-2 cursor-pointer"><RadioGroupItem value="verbal" data-testid="section-verbal" /><span>القدرات اللفظية</span></label>
                            </RadioGroup>
                        </div>
                        <div>
                            <Label className="mb-2 block">التصنيف (اختياري)</Label>
                            <Select value={categoryId || "__none__"} onValueChange={(v) => setCategoryId(v === "__none__" ? "" : v)}>
                                <SelectTrigger data-testid="category-select"><SelectValue placeholder="بدون تصنيف" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="__none__">— بدون تصنيف —</SelectItem>
                                    {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <Label>الفصول المستهدفة</Label>
                            <button type="button" onClick={selectAll} data-testid="btn-toggle-all-classes" className="text-xs text-[#1B2A4E] hover:underline">
                                {selectedClasses.length === classes.length ? "إلغاء التحديد" : "تحديد جميع الفصول"}
                            </button>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 border border-slate-200 rounded-lg p-3">
                            {classes.length === 0 && <div className="col-span-full text-center text-sm text-slate-400 py-4">لا توجد فصول مسندة</div>}
                            {classes.map((c) => (
                                <label key={c.id} className="flex items-center gap-2 p-2 rounded hover:bg-slate-50 cursor-pointer">
                                    <Checkbox checked={selectedClasses.includes(c.id)} onCheckedChange={() => toggle(c.id)} data-testid={`class-${c.id}`} />
                                    <span className="text-sm">{c.name}</span>
                                </label>
                            ))}
                        </div>
                    </div>

                    <div>
                        <Label className="mb-2 block">محتوى الموضوع</Label>
                        <TipTapEditor value={content} onChange={setContent} />
                    </div>

                    <div>
                        <Label className="mb-2 block">المرفقات</Label>
                        <AttachmentUploader value={attachments} onChange={setAttachments} />
                    </div>

                    {/* Quiz section */}
                    <div className="border border-slate-200 rounded-xl p-4 bg-slate-50/40">
                        <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                                <ClipboardCheck className="w-5 h-5 text-[#C8102E]" />
                                <div>
                                    <Label className="text-base">اختبار اختيار من متعدد</Label>
                                    <p className="text-xs text-slate-500 mt-0.5">تصحيح تلقائي فوري للطالبة</p>
                                </div>
                            </div>
                            <label className="flex items-center gap-2 cursor-pointer">
                                <Checkbox checked={quiz.enabled} onCheckedChange={(v) => setQuiz((q) => ({ ...q, enabled: !!v }))} data-testid="quiz-toggle" />
                                <span className="text-sm font-medium">{quiz.enabled ? "مفعّل" : "غير مفعّل"}</span>
                            </label>
                        </div>
                        {quiz.enabled && (
                            <div className="space-y-3">
                                {quiz.questions.length === 0 && <div className="text-sm text-slate-400 text-center py-4">أضيفي أول سؤال</div>}
                                {quiz.questions.map((q, qi) => (
                                    <div key={q.id} className="bg-white border border-slate-200 rounded-lg p-4 space-y-3">
                                        <div className="flex items-start gap-2">
                                            <Badge className="bg-[#1B2A4E] text-white hover:bg-[#1B2A4E] font-mono-num">{qi + 1}</Badge>
                                            <Textarea data-testid={`q-text-${qi}`} value={q.text} onChange={(e) => updQ(qi, { text: e.target.value })} placeholder="نص السؤال..." rows={2} className="resize-none flex-1" />
                                            <button type="button" onClick={() => rmQ(qi)} className="p-1.5 rounded text-[#C8102E] hover:bg-[#FFF1F2]" data-testid={`rm-q-${qi}`}><Trash2 className="w-4 h-4" /></button>
                                        </div>
                                        <div className="space-y-2 pr-8">
                                            {q.choices.map((ch, ci) => (
                                                <label key={ci} className={`flex items-center gap-2 rounded-lg p-2 border ${q.correct_index === ci ? "border-emerald-400 bg-emerald-50/60" : "border-slate-200"}`}>
                                                    <input type="radio" name={`correct-${qi}`} checked={q.correct_index === ci} onChange={() => updQ(qi, { correct_index: ci })} className="w-4 h-4 accent-emerald-600" data-testid={`correct-${qi}-${ci}`} />
                                                    <Input value={ch} onChange={(e) => updChoice(qi, ci, e.target.value)} placeholder={`الخيار ${ci + 1}`} className="border-0 h-8 bg-transparent" data-testid={`choice-${qi}-${ci}`} />
                                                    {q.correct_index === ci && <Badge variant="outline" className="text-emerald-700 border-emerald-300 text-[10px]">الإجابة الصحيحة</Badge>}
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                                <Button type="button" variant="outline" onClick={addQ} data-testid="btn-add-q" className="w-full"><Plus className="w-4 h-4 ml-1" /> إضافة سؤال</Button>
                            </div>
                        )}
                    </div>

                    <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
                        <Button type="button" variant="outline" onClick={() => nav("/trainer/topics")}>إلغاء</Button>
                        <Button onClick={submit} disabled={busy} data-testid="btn-publish-topic" className="bg-[#1B2A4E] hover:bg-[#0F172A] h-11 px-8">
                            <Save className="w-4 h-4 ml-2" />
                            {busy ? "جارٍ الحفظ..." : (editing ? "حفظ التعديلات" : "نشر الموضوع")}
                        </Button>
                    </div>
                </div>
            </main>
        </div>
    );
}
